// UploadButton — reusable upload widget that posts a file to /api/upload and
// hands the resolved public URL back via `onUploaded`.
//
// Props:
//   - onUploaded(url): callback once the upload succeeds; `url` is fully
//     qualified ($REACT_APP_BACKEND_URL + /api/files/...).
//   - accept: file-type filter, passed to <input accept> AND verified
//     client-side by mime type. Restrict to "image/png,image/jpeg" for
//     booking reference images; leave as "image/*" for general use.
//   - label / testId: customizable per call site.
import React, { useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Upload } from "lucide-react";

export default function UploadButton({ onUploaded, label = "Upload image", testId = "upload-image-btn", accept = "image/*" }) {
  const ref = useRef();
  const [loading, setLoading] = React.useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (accept && accept !== "image/*") {
      const allowed = accept.split(",").map((s) => s.trim());
      if (!allowed.includes(file.type)) {
        toast.error(`Only ${allowed.join(", ")} accepted`);
        if (ref.current) ref.current.value = "";
        return;
      }
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      onUploaded(url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setLoading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={handle} data-testid={`${testId}-input`} />
      <button type="button" className="btn-outline" onClick={() => ref.current?.click()} disabled={loading} data-testid={testId}>
        <Upload size={14}/> {loading ? "Uploading..." : label}
      </button>
    </>
  );
}
